import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import * as HomeActions from 'src/app/store/home/home.actions';
import { Observable, Subscription, filter } from 'rxjs';
import { Scenery } from 'src/app/model/scenery';
import { selectActions, selectSceneryAdded, selectSteps } from 'src/app/store/scenery/scenery.selector';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { Site } from 'src/app/model/site';
import { SceneryStep } from 'src/app/model/scenery-step';
import * as SceneryActions from 'src/app/store/scenery/scenery.actions';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { StepDialogComponent } from '../step-dialog/step.dialog.component';
import { SceneryActionType } from 'src/app/model/scenery-action-type';
import { SceneryAction } from 'src/app/model/scenery-action';
import { SCENERY_ACTION_TYPES } from 'src/app/conf/scenery';
import { StandardDesignParameters } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-scenery',
	templateUrl: './scenery.component.html',
	providers: [ConfirmationService, DialogService]
})
export class SceneryComponent implements OnInit, OnDestroy {
	@Input() sceneryStep: SceneryStep[];
	@Input() scenery: Scenery;
	// Observables and subscriptions
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	sceneryAdded$: Observable<number | undefined>;
	sceneryAddedSub: Subscription | undefined;
	sceneryStep$: Observable<SceneryStep[] | undefined>;
	sceneryStepSub: Subscription | undefined;
	sceneryActions$: Observable<SceneryAction[] | undefined>;
	sceneryActionsSub: Subscription | undefined;
	actionTypes: SceneryActionType[] = SCENERY_ACTION_TYPES;

	currentSite: Site;
    actions: SceneryAction[];

	ref: DynamicDialogRef | undefined;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;

	constructor(
        private store: Store<AppState>,
        private translate: TranslateService,
        public dialogService: DialogService,
        public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
        this.siteSub?.unsubscribe();
        this.sceneryAddedSub?.unsubscribe();
        this.sceneryStepSub?.unsubscribe();
        this.sceneryActionsSub?.unsubscribe();
	}

	initSelectors(): void{
        // Store selectors
        this.site$ = this.store.select(selectCurrentSite);
        this.sceneryAdded$ = this.store.select(selectSceneryAdded);
        this.sceneryStep$ = this.store.select(selectSteps);
        this.sceneryActions$ = this.store.select(selectActions);
	}

	subscribeAll(): void {
		this.siteSub = this.site$?.pipe(filter(site => !!site))
			.subscribe(site => {
				this.currentSite = site;
			});
		this.sceneryAddedSub = this.sceneryAdded$?.subscribe(sceneryAdded => {
			if (sceneryAdded > 0) {
				const s = new Scenery(this.currentSite.Id);
				s.Id = sceneryAdded;
			}
		});
		this.sceneryStepSub = this.sceneryStep$?.subscribe(
			(sceneryStep) => {
				this.sceneryStep = sceneryStep;
			}
		);
        this.sceneryActionsSub = this.sceneryActions$?.pipe(filter(actions => !!actions)).subscribe(
            actions => {
                this.actions = actions;
            }
        );
	}

	changeTab(e) {
		this.store.dispatch(HomeActions.selectScenariosTab({ index: e.index }));
	}

	getDeviceName(step: SceneryStep): string {
		if (this.currentSite && this.currentSite.Devices) {
			const device = this.currentSite.Devices.find(d => d.Id === step.DeviceId);
			return device ? device.Name : '';
		}
		return '';
	}

	getActionTypeTxt(step: SceneryStep): string {
        const action = this.actions.find(a => a.StepId === step.Id);
		const actionType = this.getActionType(action);
        return this.translate.instant(actionType.Name);
	}

	getActionTypeColorTag(step: SceneryStep): string {
      const action = this.actions.find(a => a.StepId === step.Id);
		const actionType = this.getActionType(action);
		switch (actionType.Id) {
			case 2: {
				return 'w-13rem p-tag-lightBlue0220';
			}
			case 3: {
				return 'w-13rem p-tag-lightBlue0240';
			}
			case 1: {
				return 'w-13rem p-tag-lightBlue0260';
			}
			default: {
				return 'w-13rem p-tag-darkBlue08';
			}
		}
	}

	getActionType(action: SceneryAction): SceneryActionType {
        if (action !== undefined) {
            if (action.CommandId) {
                return this.actionTypes[0];
            } else if (action.WaitMs) {
                return this.actionTypes[1];
            } else if (action.SettingId) {
                return this.actionTypes[2];
            } else {
                return this.actionTypes[0];
            }
        }
        return this.actionTypes[0];
	}

	getAction(step: SceneryStep): string {
        return step.Description;
	}

	deleteStep(step: SceneryStep): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
            header: this.translate.instant('DELETE_STEP'),
            message: this.translate.instant('DELETE_STEP_WARNING'),
            acceptLabel: this.translate.instant('CONFIRM'),
            rejectLabel: this.translate.instant('CANCEL'),
            icon: 'pi cm-alert',
            accept: () => {
                this.store.dispatch(SceneryActions.deleteStep({ stepId: step.Id }));
            },
            reject: () => {
            }
		});
	}

	addStep(): void {
		this.store.dispatch(SceneryActions.resetStepDialog());
		this.ref = this.dialogService.open(StepDialogComponent, {
			header: this.translate.instant('ADD_STEP'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
			data: {
				scenery: this.scenery,
			},
		});
	}

	editStep(step: SceneryStep): void {
        this.store.dispatch(SceneryActions.resetStepDialog());
		this.ref = this.dialogService.open(StepDialogComponent, {
			header: this.translate.instant('EDIT_STEP'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
			data: {
				step,
				scenery: this.scenery,
			},
		});
	}

	rowReorder() {
		for (let i = 0; i < this.sceneryStep.length; i++) {
			this.sceneryStep[i].Index = i;
		}
		this.store.dispatch(SceneryActions.reorderSteps({ steps: this.sceneryStep }));
	}

	// design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= StandardDesignParameters.MIN_MD_WIDTH);
	}
}
